import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import MapLoader from 'components/Loader'
import { useSites } from 'store'
import { LoaderError } from 'components/global/LoaderError'
import { OPERATION_ACCOUNTS_METRICS } from './operations-gql/models/query-gql'

const DEFAULT_COUNTING = {
  nb_lines: 0,
  nb_lines_ok: 0,
  nb_accesspoints: 0,
  nb_switches: 0,
  nb_chromecasts: 0,
  nb_tvs: 0,
  nb_esls: 0,
  nb_firewalls: 0,
  nb_cctv_cameras: 0,
  nb_cctv_servers: 0,
  nb_inverters: 0,
  nb_active_users: 0,
  nb_accesspoints_ok: 0,
  nb_chromecasts_ok: 0,
  nb_esls_ok: 0,
  nb_switches_ok: 0,
  nb_tvs_ok: 0,
  nb_firewalls_ok: 0,
  nb_cctv_cameras_ok: 0,
  nb_cctv_servers_ok: 0,
  nb_inverters_ok: 0,
  available_level: 'notReferenced',
  last_seen: null
}

const createEquipmentMetrics = (counting, available_level, last_seen) => ({
  nb_lines: counting.line_ok + counting.line_unreachable,
  nb_lines_ok: counting.line_ok,
  nb_accesspoints: counting.accesspoint_ok + counting.accesspoint_unreachable,
  nb_switches: counting.switch_ok + counting.switch_unreachable,
  nb_chromecasts: counting.chromecast_ok + counting.chromecast_unreachable,
  nb_firewalls: counting.firewall_ok + counting.firewall_unreachable,
  nb_tvs: counting.tv_ok + counting.tv_unreachable,
  nb_esls: counting.esl_ok + counting.esl_unreachable,
  nb_cctv_cameras: counting.cctv_camera_ok + counting.cctv_camera_unreachable,
  nb_cctv_servers: counting.cctv_server_ok + counting.cctv_server_unreachable,
  nb_inverters: counting.pcd_ok  + counting.pcd_unreachable,
  nb_active_users: counting.active_users,
  nb_firewalls_ok: counting.firewall_ok,
  nb_esls_ok: counting.esl_ok,
  nb_accesspoints_ok: counting.accesspoint_ok,
  nb_switches_ok: counting.switch_ok,
  nb_chromecasts_ok: counting.chromecast_ok,
  nb_tvs_ok: counting.tv_ok,
  nb_cctv_cameras_ok: counting.cctv_camera_ok,
  nb_cctv_servers_ok: counting.cctv_server_ok,
  nb_inverters_ok: counting.pcd_ok,
  available_level,
  last_seen
})

const createPosition = (location) => ({
  lng: location.longitude,
  lat: location.latitude
})

export const processRegularSites = (sites, accounts) => (
  sites.map(site => {
    const { counting, available_level, last_seen } = accounts.find(({ site: { id } }) => site.id === id) || {}
    return {
      ...site,
      position: createPosition(site.location),
      equipment: counting ? createEquipmentMetrics(counting, available_level, last_seen) : DEFAULT_COUNTING
    }
  })
)

const LoaderMetrics = ({loader, children, hideOnError = false}) => {
  const [{selected}] = useSites()
  const { focusId } = useParams()
  const sfIds = useMemo(() => {
    return selected.map(({sf_id}) => sf_id)
  }, [selected])
  const { loading, data, error } = useQuery(OPERATION_ACCOUNTS_METRICS, {
    variables: {
      sf_ids: sfIds
    },
  })

  const sites = useMemo(() => {
    if (!data) return []

    const filteredSites = data.accounts
      .filter(({ site }) => Boolean(site.location))
      .map(({ site, available_level }) => ({
        ...site,
        metrics: { available_level }
      }))

    return processRegularSites(filteredSites, data.accounts)
  }, [data, selected, focusId])
  if (loading) return loader || <MapLoader />
  if (error) return hideOnError ? loader : <LoaderError i18nkey={'supervision.tables.loader_sites_error'}>{error.message}</LoaderError>
  return children({ sites })
}

const LoaderSitesMetrics = ({ loader, children, hideOnError }) => (
  <LoaderMetrics loader={loader} hideOnError={hideOnError}>
    {({ sites }) => children({ sites })}
  </LoaderMetrics>
)

export default LoaderSitesMetrics
